import * as amplitude from '@amplitude/analytics-browser';

import type { PublicRuntimeConfig } from 'app.types';
import getConfig from 'next/config';

import { getDayOfWeek, getPartOfDay } from 'utils/getPartOfTime';
import getUTM from 'utils/getUTM';

const { publicRuntimeConfig } = getConfig();
const { analytics }: PublicRuntimeConfig = publicRuntimeConfig;
const logQueue: (() => void)[] = [];
let extraParams = {};

let isAmplitudeInitialized = false;

export function initAmplitude({
  userId,
  currentExperiments,
  extra,
  plugins,
}: {
  userId?: number;
  currentExperiments: Record<string, string>;
  extra: {
    jwt_user_id?: null | number;
    device_type?: string;
    device_os?: string;
    device_id?: string;
    domain?: string;
    screen_resolution_height?: number;
    screen_resolution_weight?: number;
  };
  plugins?: amplitude.Types.Plugin<amplitude.Types.BrowserClient, amplitude.Types.BrowserConfig>[] | undefined
}): amplitude.Types.BrowserClient {
  const { jwt_user_id, device_id, ...rest } = extra;
  extraParams = rest;

  const id = userId || jwt_user_id;

  if (plugins && plugins.length > 0) {
    plugins.map((plugin) => {
      amplitude.add(plugin);
    })
  }

  const options: amplitude.Types.BrowserOptions = {
    defaultTracking: false,
    transport: navigator.sendBeacon !== undefined ? 'beacon': 'fetch',
  };

  if (device_id) {
    options.deviceId = device_id;
  }
  
  const userIdOption = id ? String(id) : undefined;

  amplitude.init(analytics.amplitudeApiKey, userIdOption, options).promise.then(() => {
    isAmplitudeInitialized = true;
  });

  if (logQueue.length > 0) {
    logQueue.forEach(sendEvent => {
      if (typeof sendEvent === 'function') {
        sendEvent();
      }
    });
    logQueue.length = 0;
  }

  return amplitude;
}

export function setUserAmplitude(userId: number): void {
  amplitude.setUserId(String(userId))
  return;
}

export function setUserPropertyAmplitude(property: string, value: string | number): void {
  const identify = new amplitude.Identify();
  identify.set(property, value);
  amplitude.identify(identify);
  return;
}

export default function logEvent(
  event: string,
  eventProperties: Record<string, unknown> = {},
): void {
  if (!analytics.amplitudeApiKey) {
    console.info('analytics disabled');
    return;
  }
  eventProperties.day_time = getPartOfDay();
  eventProperties.week_day = getDayOfWeek();
  eventProperties.url = window.location.pathname;
  eventProperties.referrer = document.referrer;
  const UTM = getUTM();
  eventProperties = { ...eventProperties, ...UTM };

  const sendEvent = (): void => {
    const props = { ...eventProperties, ...extraParams };
    amplitude.track(event, props);
    return;
  };

  if (isAmplitudeInitialized) {
    sendEvent();
  } 
  else {
    logQueue.push(sendEvent);
  }
}
